import { enrichSpellActionData } from './combat/SpellActionReference';

const normalizeText = (value) => String(value ?? '').trim().toLowerCase();

const collectCharacterText = (combatant) => {
  try {
    return normalizeText(JSON.stringify({
      features: combatant?.features,
      classFeatures: combatant?.classFeatures,
      spells: combatant?.spells,
      spellcasting: combatant?.spellcasting,
      characterData: combatant?.characterData,
      entityPayload: combatant?.entityPayload,
    }));
  } catch (_) {
    return '';
  }
};

export const isFamiliarToken = (token) => {
  const identity = normalizeText(`${token?.role || ''} ${token?.kind || ''} ${token?.type || ''} ${token?.name || ''}`);
  return token?.isFamiliar === true || identity.includes('familiar');
};

export const isFamiliarPresent = (token) => (
  Boolean(token)
  && token?.familiarState !== 'dismissed'
  && token?.isDismissed !== true
  && token?.visible !== false
);

export const findOwnedFamiliar = (combatant, tokens = []) => {
  if (!combatant) return null;
  const familiarTokens = (Array.isArray(tokens) ? tokens : []).filter(isFamiliarToken);
  const exact = familiarTokens.find((token) => (
    String(token?.ownerTokenId || '') === String(combatant.id || '')
    || (combatant.combatantId && String(token?.ownerCombatantId || '') === String(combatant.combatantId))
  ));
  if (exact) return exact;
  const knowsFindFamiliar = collectCharacterText(combatant).includes('find familiar');
  return knowsFindFamiliar && familiarTokens.length === 1 ? familiarTokens[0] : null;
};

export const isTouchSpellAction = (action) => {
  if (normalizeText(action?.type) !== 'spell') return false;
  const range = normalizeText(action?.range || action?.rangeType || action?.originalAction?.range);
  const description = normalizeText(`${action?.description || ''} ${action?.desc || ''} ${action?.originalAction?.description || ''}`);
  return range === 'touch'
    || range.startsWith('touch ')
    || /\b(?:you touch|creature you touch|touch a creature|melee spell attack)\b/.test(description);
};

export const distanceFeetBetweenTokens = (first, second, gridSize = 50, feetPerCell = 5) => {
  const firstX = Number(first?.x ?? (Number(first?.gridX ?? first?.grid?.x) * gridSize));
  const firstY = Number(first?.y ?? (Number(first?.gridY ?? first?.grid?.y) * gridSize));
  const secondX = Number(second?.x ?? (Number(second?.gridX ?? second?.grid?.x) * gridSize));
  const secondY = Number(second?.y ?? (Number(second?.gridY ?? second?.grid?.y) * gridSize));
  if (![firstX, firstY, secondX, secondY].every(Number.isFinite)) return Number.POSITIVE_INFINITY;
  return Math.max(Math.abs(firstX - secondX), Math.abs(firstY - secondY)) / Math.max(1, gridSize) * feetPerCell;
};

export const findFamiliarReappearancePosition = (owner, tokens = [], gridSize = 50, feetPerCell = 5) => {
  const originX = Number(owner?.x ?? (Number(owner?.gridX ?? owner?.grid?.x) * gridSize));
  const originY = Number(owner?.y ?? (Number(owner?.gridY ?? owner?.grid?.y) * gridSize));
  const occupied = new Set((Array.isArray(tokens) ? tokens : [])
    .filter((token) => token?.visible !== false && token?.familiarState !== 'dismissed')
    .map((token) => `${Math.round(Number(token?.x ?? 0) / gridSize)},${Math.round(Number(token?.y ?? 0) / gridSize)}`));
  const originGridX = Math.round((Number.isFinite(originX) ? originX : 0) / gridSize);
  const originGridY = Math.round((Number.isFinite(originY) ? originY : 0) / gridSize);
  const maximumRadius = Math.max(1, Math.floor(30 / Math.max(1, feetPerCell)));
  for (let radius = 1; radius <= maximumRadius; radius += 1) {
    for (let y = -radius; y <= radius; y += 1) {
      for (let x = -radius; x <= radius; x += 1) {
        if (Math.max(Math.abs(x), Math.abs(y)) !== radius) continue;
        const gridX = Math.max(0, originGridX + x);
        const gridY = Math.max(0, originGridY + y);
        if (!occupied.has(`${gridX},${gridY}`)) return { x: gridX * gridSize, y: gridY * gridSize, gridX, gridY };
      }
    }
  }
  return { x: originGridX * gridSize, y: originGridY * gridSize, gridX: originGridX, gridY: originGridY };
};

export const buildFamiliarActions = (combatant, tokens = [], combatActions = []) => {
  const familiar = findOwnedFamiliar(combatant, tokens);
  if (!familiar) return [];
  const present = isFamiliarPresent(familiar);
  const familiarName = familiar.name || 'Familiar';
  const base = { familiarTokenId: familiar.id, familiarOwnerTokenId: combatant?.id, source: 'find-familiar' };
  const utilityActions = present ? [
    {
      ...base,
      name: 'See Through Familiar Senses',
      type: 'bonus',
      actionType: 'bonus_action',
      activationType: 'bonus action',
      familiarAction: 'perceive',
      description: `See through ${familiarName}'s eyes and hear what it hears until the start of your next turn.`,
    },
    {
      ...base,
      name: 'Dismiss Familiar',
      type: 'action',
      actionType: 'action',
      activationType: 'action',
      familiarAction: 'dismiss',
      magicAction: true,
      description: `Use a Magic Action to dismiss ${familiarName} to a pocket dimension.`,
    },
  ] : [{
    ...base,
    name: 'Reappear Familiar',
    type: 'action',
    actionType: 'action',
    activationType: 'action',
    familiarAction: 'reappear',
    magicAction: true,
    placementRangeFeet: 30,
    description: `Use a Magic Action to make ${familiarName} reappear in an unoccupied space within 30 feet.`,
  }];

  const deliveryActions = present
    ? (Array.isArray(combatActions) ? combatActions : []).filter(isTouchSpellAction).map((spell) => {
      const enrichedSpell = enrichSpellActionData(spell);
      return {
        ...enrichedSpell,
        ...base,
        displayName: `${enrichedSpell.name} through ${familiarName}`,
        type: 'spell',
        actionType: 'reaction',
        activationType: 'reaction',
        castingTime: 'reaction',
        familiarAction: 'deliver_touch_spell',
        familiarDelivery: true,
        range: 'Touch',
        originalAction: spell,
        description: `${enrichedSpell.description || enrichedSpell.desc || ''}\nDelivered from ${familiarName}'s space using your reaction.`.trim(),
      };
    })
    : [];
  return [...utilityActions, ...deliveryActions];
};

export const buildFamiliarOriginAttacker = (owner, familiar) => {
  if (!owner || !isFamiliarPresent(familiar)) return owner;
  const gridX = familiar.gridX ?? familiar.grid?.x;
  const gridY = familiar.gridY ?? familiar.grid?.y;
  return {
    ...owner,
    x: familiar.x,
    y: familiar.y,
    gridX,
    gridY,
    grid: familiar.grid ?? (Number.isFinite(Number(gridX)) && Number.isFinite(Number(gridY)) ? { x: Number(gridX), y: Number(gridY) } : owner.grid),
    familiarDeliveryOrigin: familiar.id,
  };
};

export default buildFamiliarActions;
